import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { POSTS } from "@/data/posts";
import { Reveal } from "@/components/landing/Reveal";

export const PostCard = ({ post }) => (
  <article
    data-testid={`blog-card-${post.slug}`}
    className="group flex h-full flex-col rounded-3xl border border-splurge-lavender bg-white p-7 transition-all duration-300 hover:-translate-y-1.5 hover:border-splurge-violet/50 hover:shadow-xl hover:shadow-splurge-primary/10"
  >
    <div className="flex flex-wrap items-center gap-3 text-xs font-bold">
      <span className="rounded-full bg-splurge-lavender px-3 py-1 text-splurge-dark">
        {post.category}
      </span>
      <span className="flex items-center gap-1.5 text-splurge-ink/40">
        <Clock className="h-3.5 w-3.5" aria-hidden="true" />
        {post.readMinutes} min read
      </span>
    </div>
    <h3 className="mt-5 text-xl font-extrabold leading-snug tracking-tight text-splurge-ink">
      <Link
        to={`/blog/${post.slug}`}
        data-testid={`blog-card-link-${post.slug}`}
        className="transition-colors group-hover:text-splurge-primary"
      >
        {post.title}
      </Link>
    </h3>
    <p className="mt-3 flex-1 text-sm leading-relaxed text-splurge-ink/60">{post.excerpt}</p>
    <div className="mt-6 flex items-center justify-between border-t border-splurge-lavender/80 pt-4">
      <time dateTime={post.dateISO} className="text-xs font-semibold text-splurge-ink/45">
        {post.dateLabel}
      </time>
      <Link
        to={`/blog/${post.slug}`}
        aria-label={`Read article: ${post.title}`}
        className="inline-flex items-center gap-1.5 text-sm font-bold text-splurge-primary transition-transform duration-200 group-hover:translate-x-0.5"
      >
        Read article
        <ArrowRight className="h-4 w-4" aria-hidden="true" />
      </Link>
    </div>
  </article>
);

export const Blog = () => {
  useEffect(() => {
    document.title = "Blog | Splurge Finance";
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute(
        "content",
        "Money tips for students in India: splitting shared expenses, mindful spending, budgeting, and financial literacy from the Splurge team."
      );
  }, []);

  return (
    <main id="main" className="bg-splurge-bg">
      <section className="relative overflow-hidden">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-40 -top-40 h-[420px] w-[420px] rounded-full bg-splurge-violet/20 blur-3xl"
        />
        <div className="mx-auto max-w-7xl px-5 pb-14 pt-16 sm:px-8 lg:pb-16 lg:pt-24">
          <Reveal>
            <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-splurge-primary">
              The Splurge blog
            </p>
          </Reveal>
          <Reveal delay={80}>
            <h1 className="mt-4 max-w-3xl text-4xl font-extrabold leading-[1.08] tracking-tight text-splurge-ink sm:text-5xl lg:text-6xl">
              Money tips made for{" "}
              <span className="bg-gradient-to-r from-splurge-dark via-splurge-primary to-splurge-violet bg-clip-text text-transparent">
                student life.
              </span>
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-splurge-ink/60 md:text-lg">
              Practical reads on shared expenses, mindful spending, budgeting, and financial
              literacy, written for students in India, not finance bros.
            </p>
          </Reveal>
        </div>
      </section>

      <section aria-label="All articles" className="mx-auto max-w-7xl px-5 pb-24 sm:px-8 lg:pb-32">
        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
          {POSTS.map((post, i) => (
            <Reveal key={post.slug} delay={(i % 3) * 100}>
              <PostCard post={post} />
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
};
